"use strict";

(function () {
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function FloatingContact(root) {
    this.root = root;
    this.launcher = root.querySelector('[data-fcw-launcher]');
    this.panel = root.querySelector('[data-fcw-panel]');
    this.modal = root.querySelector('[data-fcw-modal]');
    this.modalTitle = root.querySelector('[data-fcw-modal-title]');
    this.formHolder = root.querySelector('[data-fcw-form-holder]');
    this.fallback = root.querySelector('[data-fcw-fallback]');
    this.lastFocused = null;

    // The contact form app only renders one embed per ID, so when the page
    // already shows the form (contact page) drop our copy and send the widget
    // buttons to the form that is on the page instead.
    this.pageEmbed = this.findPageEmbed();
    if (this.pageEmbed && this.formHolder) this.formHolder.innerHTML = '';

    this.bind();
  }

  FloatingContact.prototype.findPageEmbed = function () {
    var self = this;
    var embeds = Array.prototype.filter.call(
      document.querySelectorAll('[contactform-embed]'),
      function (embed) {
        return !self.root.contains(embed);
      }
    );
    return embeds[0] || null;
  };

  FloatingContact.prototype.bind = function () {
    var self = this;

    if (this.launcher) {
      this.launcher.addEventListener('click', function () {
        self.togglePanel();
      });
    }

    this.root.querySelectorAll('[data-fcw-close-panel]').forEach(function (button) {
      button.addEventListener('click', function () {
        self.closePanel();
      });
    });

    this.root.querySelectorAll('[data-fcw-open-form]').forEach(function (button) {
      button.addEventListener('click', function () {
        self.openModal(button.getAttribute('data-fcw-open-form'));
      });
    });

    this.root.querySelectorAll('[data-fcw-close-modal]').forEach(function (button) {
      button.addEventListener('click', function () {
        self.closeModal();
      });
    });

    // Close the panel when clicking anywhere outside of the widget.
    document.addEventListener('click', function (event) {
      if (!self.isPanelOpen()) return;
      if (self.root.contains(event.target)) return;
      self.closePanel();
    });

    document.addEventListener('keyup', function (event) {
      if (event.key !== 'Escape') return;
      if (self.isModalOpen()) {
        self.closeModal();
      } else if (self.isPanelOpen()) {
        self.closePanel();
        if (self.launcher) self.launcher.focus();
      }
    });

    if (this.modal) {
      this.modal.addEventListener('keydown', function (event) {
        if (event.key === 'Tab') self.trapFocus(event);
      });
    }
  };

  FloatingContact.prototype.isPanelOpen = function () {
    return this.root.classList.contains('is-open');
  };

  FloatingContact.prototype.togglePanel = function () {
    if (this.isPanelOpen()) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  };

  FloatingContact.prototype.openPanel = function () {
    this.root.classList.add('is-open');
    if (this.launcher) this.launcher.setAttribute('aria-expanded', 'true');
    if (this.panel) this.panel.removeAttribute('aria-hidden');
  };

  FloatingContact.prototype.closePanel = function () {
    this.root.classList.remove('is-open');
    if (this.launcher) this.launcher.setAttribute('aria-expanded', 'false');
    if (this.panel) this.panel.setAttribute('aria-hidden', 'true');
  };

  FloatingContact.prototype.isModalOpen = function () {
    return !!this.modal && this.modal.classList.contains('is-open');
  };

  FloatingContact.prototype.openModal = function (title) {
    if (this.pageEmbed) {
      this.closePanel();
      this.pageEmbed.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!this.modal) return;

    this.lastFocused = document.activeElement;
    if (title && this.modalTitle) this.modalTitle.textContent = title;

    this.closePanel();
    this.modal.classList.add('is-open');
    this.modal.removeAttribute('aria-hidden');
    document.body.classList.add('fcw-scroll-lock');

    // The form is injected by the contact form app: give embedded iframes a
    // chance to measure themselves now that the container is visible.
    window.dispatchEvent(new Event('resize'));
    this.checkFormRendered();
    this.watchFormSize();

    var focusTarget = this.modal.querySelector(FOCUSABLE);
    if (focusTarget) focusTarget.focus();
  };

  FloatingContact.prototype.closeModal = function () {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('fcw-scroll-lock');
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }
  };

  // If the app never rendered the embed (script blocked, duplicate embed on the
  // page, …) show the link to the contact page instead of an empty modal.
  FloatingContact.prototype.checkFormRendered = function () {
    var self = this;
    if (!this.formHolder || !this.fallback) return;

    setTimeout(function () {
      // The app either fills the embed div or swaps it out entirely, so look
      // for anything rendered rather than for the embed element itself.
      var hasContent =
        !!self.formHolder.querySelector('form, iframe, input') ||
        self.formHolder.textContent.trim().length > 0;
      self.fallback.classList.toggle('is-visible', !hasContent);
    }, 2000);
  };

  // The contact form app hard-codes its width (.bcontact-md is width:660px
  // !important) and clips the overflow with overflow:hidden, so the form gets
  // visibly cut. Stylesheet rules can't beat the app's !important reliably, so
  // measure what it actually rendered and clamp the offenders inline, which
  // does win. Compare rectangles rather than scrollWidth: a clipped element
  // reports no overflow at all.
  FloatingContact.prototype.fitForm = function () {
    var holder = this.formHolder;
    if (!holder) return;

    var available = holder.clientWidth;
    if (!available) return;

    for (var pass = 0; pass < 3; pass++) {
      var holderRect = holder.getBoundingClientRect();
      var elements = holder.querySelectorAll('*');
      var clamped = false;

      for (var i = 0; i < elements.length; i++) {
        var el = elements[i];
        var rect = el.getBoundingClientRect();
        if (!rect.width) continue;

        var tooWide = rect.width - available > 1;
        var pastEdge = rect.right - holderRect.right > 1;
        if (!tooWide && !pastEdge) continue;

        el.style.setProperty('box-sizing', 'border-box', 'important');
        el.style.setProperty('max-width', '100%', 'important');
        el.style.setProperty('min-width', '0', 'important');
        if (tooWide) el.style.setProperty('width', '100%', 'important');
        clamped = true;
      }

      if (!clamped) break;
    }
  };

  // The app renders asynchronously and can re-render (validation, success
  // message), so keep re-fitting rather than measuring once.
  FloatingContact.prototype.watchFormSize = function () {
    var self = this;
    var schedule = function () {
      window.requestAnimationFrame(function () {
        self.fitForm();
      });
    };

    schedule();
    [100, 400, 1200, 2500].forEach(function (delay) {
      setTimeout(schedule, delay);
    });

    if (this.formWatcher || !this.formHolder) return;

    if (typeof MutationObserver === 'function') {
      this.formWatcher = new MutationObserver(schedule);
      this.formWatcher.observe(this.formHolder, { childList: true, subtree: true });
    }

    window.addEventListener('resize', schedule);
  };

  FloatingContact.prototype.trapFocus = function (event) {
    var focusable = Array.prototype.filter.call(
      this.modal.querySelectorAll(FOCUSABLE),
      function (element) {
        return element.offsetParent !== null;
      }
    );
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  function init() {
    document.querySelectorAll('[data-fcw]').forEach(function (root) {
      if (root.dataset.fcwReady === 'true') return;
      root.dataset.fcwReady = 'true';
      new FloatingContact(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialise when the section is redrawn in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
